import { authHeaders, type WhipAuth } from './auth';
import { SILENT, type WhipLogger } from './logger';
import { WhipSession } from './WhipSession';

/**
 * Everything a WHIP session needs, and nothing it can work out for itself.
 *
 * Only `endpoint` and `stream` are required; the rest have defaults that match what the ingest stack actually accepts.
 */
export interface WhipSessionOptions {
	/**
	 * The full ingest url — `https://whip-dev.zencastrdev.com/v2/rtc/whip` for the production route, or `…/v2/rtc/whip/debug`
	 * for the one that takes identity headers directly. A page-relative path is resolved against the document.
	 *
	 * The route is not derived from `auth`, because which of the two a deployment exposes is a deployment's decision:
	 * `/whip/debug` is disabled by default in production.
	 */
	endpoint: string;
	/**
	 * What to send: exactly one video track and one audio track, which is all the transcoder will accept. Multi-track
	 * audio is plumbed as far as the gateway and then refused, so mix it down before it gets here.
	 *
	 * The stream stays the caller's. This session never stops a track.
	 */
	stream: MediaStream;
	/** Identity. Omit it only against a debug endpoint that is happy to invent one. See {@link WhipAuth}. */
	auth?: WhipAuth;
	/** Somewhere to write diagnostics. Silent without one; `console` will do. See {@link WhipLogger}. */
	logger?: WhipLogger;
	/** Negotiate the `abs-capture-time` RTP header extension. On by default; harmless where the browser lacks it. */
	absCaptureTime?: boolean;
	/**
	 * A cap on the frame short side declared to the transcoder for its ABR ladder. What the video track actually produces
	 * is used when it is smaller, and on its own when this is unset; `null` sends nothing and lets the transcoder decide.
	 *
	 * Whatever ends up declared has to fall in the range the transcoder accepts, or `start()` rejects with
	 * `invalid-options` — it answers an out-of-range value with a 400 rather than capping it.
	 */
	maxVideoShortSide?: number | null;
	/** A bitrate ceiling for the video encoding, in bits per second. Set at transceiver creation, which is the only place it holds. */
	maxBitrate?: number;
	/**
	 * What the encoder should sacrifice first under pressure. `maintain-resolution` is worth setting for content whose
	 * legibility depends on the pixel count — composited grids, shared screens, anything with text in it.
	 */
	degradationPreference?: RTCDegradationPreference;
	/** For tests. Defaults to the global `fetch`. */
	fetchImpl?: typeof fetch;
}

/**
 * Builds one WHIP session. Nothing happens on the wire until `start()`.
 *
 * ```ts
 * const session = createWhipSession({
 *   endpoint: 'https://whip-dev.zencastrdev.com/v2/rtc/whip/debug',
 *   stream,
 *   auth: { livestreamId, userId, streamKeys: [{ provider: 'youtube', key }] },
 *   logger: console,
 * });
 *
 * session.on('statechange', ({ state }) => { if (state === 'failed') restart(); });
 * await session.start();
 * await session.close();
 * ```
 *
 * Building and starting are separate so that subscribing can happen in between: every state the session ever reaches is
 * then reportable through `on('statechange')`, and there is no need for a second event channel covering negotiation.
 *
 * `start()` rejects with a {@link import('./errors').WhipError} carrying a `reason` worth branching on, and leaves
 * nothing running behind a rejection — including a server resource created before the failure.
 *
 * There is no reconnect. `PATCH` on the session resource is routed but implemented as a no-op, so a `failed` connection
 * is recovered by closing this session and creating another.
 */
export function createWhipSession(options: WhipSessionOptions): WhipSession {
	return new WhipSession({
		endpoint: options.endpoint,
		stream: options.stream,
		headers: authHeaders(options.auth),
		absCaptureTime: options.absCaptureTime ?? true,
		maxVideoShortSide: options.maxVideoShortSide,
		maxBitrate: options.maxBitrate,
		degradationPreference: options.degradationPreference,
		logger: options.logger ?? SILENT,
		fetchImpl: options.fetchImpl,
	});
}
